import { type ApolloError, useMutation } from '@apollo/client';
import type { DealFormDataWithTargeting } from '@openx/types/deal.ts';
import type { PackageFormState } from '@openx/types/package.ts';
import { TargetingFields } from '@openx/types/targeting/targeting.ts';
import { ComparisonType } from '@openx/types/targeting/targetingValuesTypes.ts';
import { produce } from 'immer';
import { useSnackbar } from 'notistack';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { type Location, useLocation, useNavigate } from 'react-router';
import { useAuth } from '../../../../components/AuthContext/context.ts';
import { ActivateAudienceDocument } from '../../../../graphql/mutation/audiences/ActivateAudience.generated.tsx';
import { CreateAudienceDocument } from '../../../../graphql/mutation/audiences/CreateAudience.generated.tsx';
import { CreateLogDocument } from '../../../../graphql/mutation/error/createLog.generated.tsx';
import { NestedErrorCode } from '../../../graphql/types.ts';
import { useLocalErrorHandling } from '../../../graphql/useLocalErrorHandling.ts';
import type { AudienceLocationState } from '../AudienceFormFields.tsx';
import type { AudienceForm } from '../types.ts';
import { filterNormalizedEmptyGroups } from '../utils/filterNormalizedEmptyGroups.ts';
import { isAudienceNameConflictError, isAudienceNamePath } from '../utils/isAudienceNameConflictError.ts';
import { mapAudienceToCreateMutation } from '../utils/mapAudienceToCreateMutation.ts';

export const useCreateAndActivate = (userId: string) => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const location: Location<AudienceLocationState> = useLocation();

  const [createAudience] = useMutation(CreateAudienceDocument, {
    refetchQueries: ['GetAudiencesList', 'GetAudienceTemplates'],
  });

  const [activateAudience] = useMutation(ActivateAudienceDocument, {
    refetchQueries: ['GetAudiencesList', 'GetAudienceTemplates'],
  });

  const [pushLog] = useMutation(CreateLogDocument);

  useLocalErrorHandling(
    CreateAudienceDocument,
    useCallback(
      error => {
        if (error.code !== NestedErrorCode.JOINT_UNIQUENESS_VIOLATION || !isAudienceNamePath(error.path)) return false;
        enqueueSnackbar(t('Audience with such name already exists'), { variant: 'error' });
        return true;
      },
      [enqueueSnackbar, t]
    )
  );

  return useCallback(
    async (values: AudienceForm) => {
      const filteredAudienceCriteria = filterNormalizedEmptyGroups(values.normalized_definition);

      if (filteredAudienceCriteria.includes.groups.length === 0) {
        enqueueSnackbar(t('At least one segment should be selected'), { variant: 'error' });

        return;
      }

      const audience = mapAudienceToCreateMutation(userId, values, filteredAudienceCriteria);

      try {
        const response = await createAudience({
          variables: {
            object: audience,
          },
        });
        const audienceId = response?.data?.insert_audience_one?.id;

        await activateAudience({ variables: { id: audienceId } });

        if (location.state?.redirectPath) {
          const formKey = location.state.redirectPath.startsWith('/deals') ? 'deal' : 'package';
          const baseState = location.state[formKey];

          if (!baseState) {
            return navigate(location.state.redirectPath);
          }

          const formObject = produce(baseState, draft => {
            const nextAudience = {
              openaudience_custom: {
                op: ComparisonType.INTERSECTS,
                val: `openaudience-${audienceId}`,
              },
            };

            if (formKey === 'deal') {
              const dealDraft = draft as DealFormDataWithTargeting;
              dealDraft.targeting.targeting[TargetingFields.AUDIENCE] = nextAudience;
            }
            if (formKey === 'package') {
              const packageDraft = draft as PackageFormState;
              packageDraft.targeting[TargetingFields.AUDIENCE] = nextAudience;
            }
          });

          return navigate(location.state.redirectPath, {
            state: {
              [formKey]: formObject ?? null,
              useCuratedTemplate: location.state?.useCuratedTemplate,
              useUniqueTargeting: location.state?.useUniqueTargeting,
            },
          });
        }

        navigate('/audiences');
        enqueueSnackbar('Audience created successfully', { variant: 'success' });
      } catch (error) {
        const apolloError = error as ApolloError;
        const { message } = apolloError;
        const isJointUniquenessViolation = isAudienceNameConflictError(apolloError);

        await pushLog({
          variables: {
            location: window.location.href,
            msg: message || 'Error when creating Audience',
            name: currentUser?.displayName,
            email: currentUser?.email,
          },
        });

        if (!isJointUniquenessViolation && message !== 'Joint uniqueness violation') {
          enqueueSnackbar('Error when creating Audience', { variant: 'error' });
        }

        if (isJointUniquenessViolation) {
          throw error;
        }
      }
    },
    [
      userId,
      createAudience,
      activateAudience,
      enqueueSnackbar,
      currentUser?.displayName,
      currentUser?.email,
      location.state?.redirectPath,
      location.state?.useUniqueTargeting,
      location.state,
      navigate,
      pushLog,
      t,
      location.state?.useCuratedTemplate,
    ]
  );
};
